import { CP_KIND, allTypedFieldsValid, normalizeFieldName } from "./cpFieldRules";

// Sharing a field we can't vouch for could print one state's value on another state's agreement.
const SHARED_OTHER_NAMES = new Set(["title", "company name"]);
// Of the recognized kinds the signer types, only phone and address are the same on every agreement; any other is asked per agreement.
const SHARED_KINDS = new Set([CP_KIND.phone, CP_KIND.address]);
// The table locks every other recognized kind, so any other one that reaches a group is labeled by its field name.
const LABEL = {
  [CP_KIND.phone]: "Phone",
  [CP_KIND.address]: "Address",
};

const spaced = (name) => String(name ?? "").replace(/[_-]+/g, " ");

// A name that already has a capital is kept as written, so "DEA" doesn't become "Dea".
export function humanizeFieldName(name) {
  const words = spaced(name).replace(/\s+/g, " ").trim();
  return words === words.toLowerCase() ? words.charAt(0).toUpperCase() + words.slice(1) : words;
}

// ENG-1865: one input per detail the signer types, applied to every agreement that has that field.
export function detailGroups(agreements) {
  const groups = [];
  const byKey = new Map();
  for (const agreement of agreements) {
    for (const entry of agreement.cpFields) {
      if (entry.locked) continue;
      const other = entry.kind === CP_KIND.other;
      const name = other ? normalizeFieldName(spaced(entry.field?.field_name)) : null;
      const perAgreement = other ? !SHARED_OTHER_NAMES.has(name) : !SHARED_KINDS.has(entry.kind);
      // Same-named fields of different input types (a date and a phone) get separate inputs: one value can't pass both checks.
      const shared = other ? `other:${entry.input}:${name}` : entry.kind;
      const key = perAgreement ? `${shared}@${agreement.secret}` : shared;
      if (!byKey.has(key)) {
        const base = LABEL[entry.kind] ?? humanizeFieldName(entry.field?.field_name);
        const group = {
          key,
          label: perAgreement ? `${base}, ${agreement.label}` : base,
          input: entry.input,
          prefill: entry.prefill ?? "",
          members: [],
        };
        byKey.set(key, group);
        groups.push(group);
      }
      byKey.get(key).members.push({ secret: agreement.secret, entryKey: entry.key });
    }
  }
  return groups;
}

// Maps the form's values back to one agreement's entry keys (cp_<field id>); an untouched box gives every member the pre-fill it shows.
export function typedFor(agreement, groups, values) {
  const typed = {};
  for (const group of groups) {
    const value = Object.prototype.hasOwnProperty.call(values, group.key) ? values[group.key] : group.prefill;
    for (const member of group.members) {
      if (member.secret === agreement.secret) typed[member.entryKey] = value;
    }
  }
  return typed;
}

export const batchDetailsValid = (agreements, groups, values) =>
  agreements.every((agreement) => allTypedFieldsValid(agreement.cpFields, typedFor(agreement, groups, values)));
